import { Button, type ButtonVariant } from '@/components/shadcn/button'
import { IconV2ArrowRightOutline } from '@icons/IconV2ArrowRightOutline'
import { Plus } from 'lucide-react'
import { ButtonExample } from './CopyableSyntaxExample'

const VARIANTS: { variant: ButtonVariant; label: string }[] = [
  { variant: 'default', label: 'Primary (default)' },
  { variant: 'outline', label: 'Secondary (outline)' },
  { variant: 'ghost', label: 'Tertiary (ghost)' },
  { variant: 'input', label: 'Input (combobox/select trigger)' },
  { variant: 'onMedia', label: 'On media' },
  { variant: 'destructive', label: 'Destructive' },
  { variant: 'link', label: 'Link' },
]

const SIZES = [
  { size: 'lg' as const, label: 'Large' },
  { size: 'default' as const, label: 'Medium' },
  { size: 'sm' as const, label: 'Small' },
]

function formatButtonSyntax({
  variant,
  size,
  disabled,
  className,
  ariaLabel,
  children,
}: {
  variant: ButtonVariant
  size?: 'default' | 'sm' | 'lg' | 'icon'
  disabled?: boolean
  className?: string
  ariaLabel?: string
  children: string
}) {
  const props: string[] = []

  if (variant !== 'default') {
    props.push(`variant="${variant}"`)
  }
  if (size && size !== 'default') {
    props.push(`size="${size}"`)
  }
  if (disabled) {
    props.push('disabled')
  }
  if (className) {
    props.push(`className="${className}"`)
  }
  if (ariaLabel) {
    props.push(`aria-label="${ariaLabel}"`)
  }

  const attrs = props.length > 0 ? ` ${props.join(' ')}` : ''
  return `<Button${attrs}>${children}</Button>`
}

function VariantRow({ variant, label }: { variant: ButtonVariant; label: string }) {
  const isOnMedia = variant === 'onMedia'
  const mediaClassName = isOnMedia ? 'rounded-sm bg-gradient-to-br from-neutral-500 to-black p-4' : undefined

  return (
    <div className="space-y-3 border-b border-border pb-6 last:border-b-0 last:pb-0">
      <h3 className="text-sm font-semibold">{label}</h3>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Enabled</p>
          <div className={mediaClassName}>
            {variant === 'input' ? (
              <ButtonExample
                syntax={formatButtonSyntax({
                  variant,
                  className: 'w-full max-w-xs',
                  children: 'Placeholder text',
                })}
              >
                <Button variant={variant} className="w-full max-w-xs">
                  <span className="text-muted-foreground">Placeholder text</span>
                </Button>
              </ButtonExample>
            ) : (
              <ButtonExample
                syntax={formatButtonSyntax({
                  variant,
                  children: '<Plus />Button<IconV2ArrowRightOutline />',
                })}
              >
                <Button variant={variant}>
                  <Plus />
                  Button
                  <IconV2ArrowRightOutline />
                </Button>
              </ButtonExample>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Disabled</p>
          <div className={mediaClassName}>
            <ButtonExample
              syntax={formatButtonSyntax({
                variant,
                disabled: true,
                children: '<Plus />Button<IconV2ArrowRightOutline />',
              })}
            >
              <Button variant={variant} disabled>
                <Plus />
                Button
                <IconV2ArrowRightOutline />
              </Button>
            </ButtonExample>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Icon only</p>
          <div className={mediaClassName}>
            <ButtonExample
              syntax={formatButtonSyntax({
                variant,
                size: 'icon',
                ariaLabel: 'Icon button',
                children: '<Plus />',
              })}
            >
              <Button variant={variant} size="icon" aria-label="Icon button">
                <Plus />
              </Button>
            </ButtonExample>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Sizes</p>
          <div
            className={
              isOnMedia
                ? 'flex flex-wrap items-center gap-2 rounded-sm bg-gradient-to-br from-neutral-500 to-black p-4'
                : 'flex flex-wrap items-center gap-2'
            }
          >
            {SIZES.map(({ size, label: sizeLabel }) => (
              <ButtonExample
                key={size}
                syntax={formatButtonSyntax({
                  variant,
                  size,
                  children: sizeLabel,
                })}
              >
                <Button variant={variant} size={size}>
                  {sizeLabel}
                </Button>
              </ButtonExample>
            ))}
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Use the copy button to copy JSX. Hover, press, and tab each button to verify interaction states.
      </p>
    </div>
  )
}

export function ButtonsSection() {
  return (
    <div className="space-y-6">
      {VARIANTS.map((item) => (
        <VariantRow key={item.variant} {...item} />
      ))}
    </div>
  )
}
